import { Link } from 'react-router-dom';

import { PictoConformite } from '@/assets/icons-april-fr/pictos';
import AprilPicto from '@/components/shared/april-picto';
import SmallSpinner from '@/components/shared/spinners/small-spinner';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { HOST_MB } from '@/lib/constants';
import { useContracts, useContractsData, useSelectedContract } from '@/hooks/api/use-contracts';
import { useCurrentUser, useIsFleetUser } from '@/hooks/api/use-user';
import useIsPWA from '@/hooks/use-is-pwa';
import usePageTitle from '@/hooks/use-page-title';
import { hasOnlyContractApporteur43397, isWakamContract } from '@/lib/contracts';

import AccesRapides from '@/pages/home/acces-rapides';
import Accidents from '@/pages/home/accidents';
import Contact from '@/pages/home/contact';
import Contracts from '@/pages/home/contracts';
import HomePWA from '@/pages/home/home-pwa';
import PromoBlocs from '@/pages/home/promo-blocs';
import TodoBloc from '@/pages/home/todo-bloc';
import Warning from '@/pages/home/warning';
import Welcome from '@/pages/home/welcome';
// Décommenter avec l'usage quand les apps stores seront prêts
// import AppDownloadBanner from '@/components/shared/app-download-banner';

const HomeFull = () => {
    usePageTitle('Accueil');
  const user = useCurrentUser();
  const contracts = useContractsData();
  const souscripteur = useSelectedContract()?.souscripteur;
  const isFleetUser = useIsFleetUser();
  // `toContract` convertit les `null` de l'API en `undefined` : on teste l'absence de valeur, pas `null`.
  const hasIncompleteBirthInfo =
    !!souscripteur && (!souscripteur.pays_naissance || !souscripteur.ville_naissance || !souscripteur.nom_naissance);
  const hasOnlyApporteur43397 = hasOnlyContractApporteur43397(contracts);
  const nbContractsNonPrelev = contracts?.filter((c) => !c.prelevement && c.situation?.statutCode === '1') || [];
  const singleContractId = nbContractsNonPrelev.length === 1 ? nbContractsNonPrelev[0].id : null;

  return (
    <div className="space-y-6">
      <Welcome firstName={user.firstname} lastName={user.lastname} titre={user.titre} userId={user.belair_id} />

      {hasIncompleteBirthInfo && (
        <Warning messageWarning="Vos informations de naissance sont incomplètes, merci de les compléter dans votre profil." />
      )}

      {nbContractsNonPrelev.length > 0 && (
        <Alert className="bg-destructive/10 text-destructive border-destructive/30 items-center [&>svg]:translate-y-0 has-[>svg]:gap-x-4">
          <AprilPicto icon={PictoConformite} size="lg" />
          <AlertDescription className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <span className="font-medium">Passez au paiement par prélèvement pour plus de simplicité et de sécurité</span>
            <Button asChild className="shrink-0 cursor-pointer">
              <Link
                to={
                  nbContractsNonPrelev.length === 1
                    ? `/mes-contrats/mon-contrat/${singleContractId}/je-passe-en-prelevement`
                    : '/mes-contrats'
                }
              >
                Je passe au prélèvement
              </Link>
            </Button>
          </AlertDescription>
        </Alert>
      )}

      <TodoBloc />

      <AccesRapides />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Contracts />
        <Accidents />
      </div>

      {HOST_MB === 43397 && !isFleetUser && <PromoBlocs isEligibleParrainage={user.eligible_parrainage} />}

      <Contact hasApporteur43397={hasOnlyApporteur43397} />

      {/* Badges stores commentés en attendant la publication des apps. Décommenter quand prêt. */}
      {/* {HOST_MB === 43397 && <AppDownloadBanner />} */}
    </div>
  );
};

/** Accueil de l'espace assuré : version allégée en PWA, complète sinon (une fois les contrats chargés). */
const Home = () => {
    const isPWA = useIsPWA();
    const contractsQuery = useContracts();
    const contracts = useContractsData();
    const user = useCurrentUser();
    const isWakam = isWakamContract(useSelectedContract());

    if (isPWA) {
        return <HomePWA firstName={user.firstname} lastName={user.lastname} titre={user.titre} contracts={contracts} isWakam={isWakam} />;
    }

    if (contractsQuery.isPending) return <SmallSpinner />;

    return <HomeFull />;
};

export default Home;
